import { ArrowForward, ContentCopy } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  MenuItem,
} from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InternalLinkButton } from '@omnea-app/fe-routing/InternalLinkButton'
import { LabeledTextField } from '@omnea-app/frontend/components/shared/form/inputs/labeled/LabeledTextField'
import { useDuplicateRequest } from './hooks/useDuplicatetRequest'

interface Props {
  requestId: string
  requestName?: string
  onClose?: () => void
}

export const DuplicateRequestMenuItem = ({ requestId, requestName, onClose }: Props) => {
  const { t } = useTranslation('requests')

  const [showDialog, setShowDialog] = useState(false)

  const [newRequestName, setNewRequestName] = useState('')

  const { mutateAsync: duplicateRequest, isPending: duplicateLoading } = useDuplicateRequest({
    requestId,
  })

  const handleOpenDialog = () => {
    setShowDialog(true)
    setNewRequestName(
      t('duplication-modal.duplicate-name-template', {
        requestName: requestName ?? t('duplication-modal.cannot-find-request-name'),
      })
    )
    onClose?.()
  }

  const handleAction = async () => {
    try {
      const newDraftId = await duplicateRequest({ body: { requestName: newRequestName } })
      setShowDialog(false)
      enqueueSnackbar(`${t('duplication-modal.success')}`, {
        variant: 'success',
        autoHideDuration: 6000,
        action: (
          <InternalLinkButton
            sx={{
              color: 'inherit',
              textTransform: 'uppercase',
            }}
            href={`/intake/${newDraftId.workflowId}?draftId=${newDraftId.id}`}
            endIcon={<ArrowForward />}
            variant="text"
          >
            {t('duplication-modal.success-action')}
          </InternalLinkButton>
        ),
      })
    } catch {
      setShowDialog(false)
    }
  }

  return (
    <>
      <MenuItem onClick={handleOpenDialog} data-testid="duplicate-request-menu-item">
        <ListItemIcon>
          <ContentCopy />
        </ListItemIcon>
        <ListItemText>{t('manage-request.duplicate')}</ListItemText>
      </MenuItem>
      {showDialog && (
        <Dialog open={showDialog} onClose={() => setShowDialog(false)}>
          <DialogTitle>{t('duplication-modal.title')}</DialogTitle>
          <DialogContent sx={{ gap: 4 }}>
            <DialogContentText>{t('duplication-modal.description')}</DialogContentText>
            <LabeledTextField
              dataTestId="duplicate-request-name-input"
              label={t('duplication-modal.duplicate-request-input-title')}
              fullWidth
              required
              value={newRequestName}
              onChange={(e) => setNewRequestName(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" color="neutral" onClick={() => setShowDialog(false)} disabled={duplicateLoading}>
              {t('duplication-modal.cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAction}
              loading={duplicateLoading}
              data-testid="duplicate-request-confirm-button"
            >
              {t('duplication-modal.duplicate')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}
